/**
 * Auth0 Callback Handler Component
 *
 * Handles the OAuth callback from Auth0 after login
 * Shows loading state while Auth0 processes the authentication
 * The Auth0Provider will automatically:
 * 1. Parse the authorization code from the URL
 * 2. Exchange it for tokens
 * 3. Store tokens in localStorage
 * 4. Call onRedirectCallback to navigate to the returnTo URL
 */

import { useAuth0 } from '@auth0/auth0-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export function AuthCallback() {
  const { error, isLoading } = useAuth0();
  const navigate = useNavigate();

  useEffect(() => {
    // If there's an error, show it and allow retry
    if (error) {
      console.error('Auth0 callback error:', error);
    }

    // If Auth0 finishes processing but we're still on /callback,
    // something went wrong with the redirect. Send user home.
    if (!isLoading && !error) {
      // Small delay to allow Auth0Provider's onRedirectCallback to fire
      const timeout = setTimeout(() => {
        console.log('Callback complete, redirecting to home');
        navigate('/');
      }, 1000);

      return () => clearTimeout(timeout);
    }
  }, [error, isLoading, navigate]);

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#090E34] p-4">
        <div className="max-w-md w-full bg-white rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-[#000050] mb-4 font-martianmono">
            Authentication Error
          </h2>
          <p className="text-gray-700 mb-4 font-ibmplexsans">
            {error.message}
          </p>
          <p className="text-sm text-gray-500 mb-6 font-ibmplexsans">
            Please try logging in again. If the problem persists, contact support.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/login')}
              className="w-full px-6 py-3 bg-[#F45F00] text-white rounded-lg hover:bg-[#F45F00]/90 transition-colors font-martianmono"
            >
              Back to Login
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-martianmono"
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state while Auth0 processes the callback
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#090E34]">
      <div className="text-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#F45F00] mx-auto mb-6"></div>
        <h2 className="text-2xl font-bold text-[#FDFDE9] mb-3 font-martianmono">
          Completing Sign In
        </h2>
        <p className="text-[#FDFDE9]/80 font-ibmplexsans">
          Please wait while we securely log you in...
        </p>
        <div className="mt-8 text-xs text-[#FDFDE9]/50 font-ibmplexmono">
          Powered by Auth0
        </div>
      </div>
    </div>
  );
}
